import { useQuery } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import styled from 'styled-components';

import Button from '@/components/Button';
import { PageHeading, SrOnly } from '@/components/Layout/Parts';
import ScrollableTable from '@/components/ScrollableTable';
import SearchBar from '@/components/SearchBar';
import { SortableTh, Table, TableContent, Th } from '@/components/Table';
import useDebounce from '@/hooks/useDebounce';
import useSortState from '@/hooks/useSortState';
import { FinanceAccountNumberResource } from '@/types/resources';
import { api } from '@/utils/api';
import { SortDirection } from '@/utils/enums';

import AccountNumberFormModal from './components/AccountNumberFormModal';
import AccountNumbersTableRow from './components/AccountNumbersTableRow';

const StyledTable = styled(Table)`
  td,
  th {
    vertical-align: middle;
    font-weight: 700;
    &:first-child {
      padding-left: 24px;
    }
    &:last-child {
      width: 64px;
      padding-right: 24px;
    }
  }
`;

const HeaderWrap = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const AccountNumbers = () => {
  const [search, setSearch] = useState('');

  const [showCreateModal, setShowCreateModal] = useState(false);

  const debouncedSearch = useDebounce(search, 300);

  const { sort, setSort, sortQueryParamObj } = useSortState({
    property: 'name',
    direction: SortDirection.DEFAULT,
  });

  const {
    data: accountNumbers,
    isError,
    isLoading,
  } = useQuery(
    [
      'finance-account-numbers',
      {
        sort,
        search: debouncedSearch,
      },
    ],
    () =>
      api.get<never, FinanceAccountNumberResource[]>(
        '/v1/finance-account-numbers',
        {
          params: {
            ...(!!search && {
              'filter[name]': debouncedSearch,
            }),
            ...sortQueryParamObj,
          },
        },
      ),
    {
      staleTime: 90 * 1000,
    },
  );

  return (
    <>
      <HeaderWrap>
        <PageHeading>Finance Account Numbers</PageHeading>
        <Button onClick={() => setShowCreateModal(true)}>Add New</Button>
      </HeaderWrap>
      <SearchBar value={search} setValue={setSearch} />
      <ScrollableTable>
        <StyledTable $fixed>
          <thead>
            <tr>
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="name"
                title="Name"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="value"
                title="Value"
              />
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="important"
                title="Important"
              />
              <Th>
                <SrOnly>Actions</SrOnly>
              </Th>
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              isError={isError}
              hasData={!!accountNumbers?.length}
              colSpan={4}
            >
              {accountNumbers?.map((accountNumber) => (
                <AccountNumbersTableRow
                  key={accountNumber.id}
                  accountNumber={accountNumber}
                />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ScrollableTable>
      <AnimatePresence>
        {showCreateModal && (
          <AccountNumberFormModal onClose={() => setShowCreateModal(false)} />
        )}
      </AnimatePresence>
    </>
  );
};

export default AccountNumbers;
